/**
 * Static data for the marketing landing page.
 * Centralizes all section content so page components stay lean.
 */
import type { ReactNode } from "react";

/* ─── Type definitions ─── */

export interface FeatureItem {
  title: string;
  desc: string;
  /** Tailwind classes for the icon wrapper bg & text colour */
  bg: string;
  /** Tailwind class for the glow blob */
  glow: string;
  /** key into <Icons /> — resolved at render time */
  iconKey: string;
  /** Inline SVG override (used for the OpenAI logo) */
  svgPath?: string;
  isNew?: boolean;
}

export interface StepItem {
  step: string;
  title: string;
  desc: string;
  iconKey: string;
  gradient: string;
}

export interface StatItem {
  value: string;
  numericValue?: number;
  suffix?: string;
  label: string;
  sub: string;
}

export interface IntegrationItem {
  name: string;
  iconKey: string;
  gradient: string;
  bg: string;
  desc: string;
}

export interface WebsitePlatformItem {
  name: string;
  src: string;
}

export interface WorkflowNode {
  label: string;
  color: string;
}

export interface TemplateCard {
  name: string;
  color: string;
}

/* ─── Providers ─── */

export const PROVIDERS = [
  { label: "OpenAI", svgPath: "M239.184 106.203a64.716 64.716 0 0 0-5.576-53.103C219.452 28.459 191 15.784 163.213 21.74A65.586 65.586 0 0 0 52.096 45.22a64.716 64.716 0 0 0-43.23 31.36c-14.31 24.602-11.061 55.634 8.033 76.74a64.665 64.665 0 0 0 5.525 53.102c14.174 24.65 42.644 37.324 70.446 31.36a64.72 64.72 0 0 0 48.754 21.744c28.481.025 53.714-18.361 62.414-45.481a64.767 64.767 0 0 0 43.229-31.36c14.137-24.558 10.875-55.423-8.083-76.483Z", viewBox: "0 0 256 260" },
  { label: "Claude", svgPath: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z", viewBox: "0 0 24 24" },
  { label: "Gemini", circleData: { cx: "95.25", cy: "95.25", r: "95.25" }, pathData: "M95.252 132.961c20.824 0 37.705-16.881 37.705-37.706S116.076 57.55 95.252 57.55 57.547 74.431 57.547 95.255s16.881 37.706 37.705 37.706z", viewBox: "0 0 190.5 190.5" },
] as const;

export const DEPLOY_TARGETS = [
  { label: "Slack", iconKey: "slack", bgClass: "bg-[#4A154B]/10 text-[#4A154B]" },
  { label: "Discord", iconKey: "discord", bgClass: "bg-[#5865F2]/10 text-[#5865F2]" },
  { label: "WhatsApp", iconKey: "whatsapp", bgClass: "bg-[#25D366]/10 text-[#25D366]" },
  { label: "Telegram", iconKey: "telegram", bgClass: "bg-[#0088cc]/10 text-[#0088cc]" },
] as const;

/* ─── AI Website Builder features list ─── */

export const WEBSITE_BUILDER_FEATURES = [
  "Prompt-to-website in seconds",
  "Live E2B sandbox preview",
  "VS Code-style code editor",
  "Iterative AI refinement",
  "React, HTML & Next.js",
  "Version history & restore",
] as const;

/* ─── Workflow diagram nodes ─── */

export const WORKFLOW_NODES: WorkflowNode[] = [
  { label: "Trigger", color: "bg-amber-100 text-amber-700 border-amber-200" },
  { label: "AI Process", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
  { label: "Condition", color: "bg-violet-100 text-violet-700 border-violet-200" },
  { label: "Action", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
];

export const WORKFLOW_FEATURES = [
  "12 node types",
  "Drag-and-drop canvas",
  "AI & logic nodes",
  "One-click activate",
] as const;

/* ─── Template marketplace cards ─── */

export const TEMPLATE_CARDS: TemplateCard[] = [
  { name: "Customer Support", color: "from-indigo-500 to-blue-600" },
  { name: "E-Commerce", color: "from-emerald-500 to-teal-600" },
  { name: "SaaS Onboard", color: "from-violet-500 to-purple-600" },
];

export const TEMPLATE_FEATURES = [
  "Ready-made templates",
  "One-click import",
  "Full customization",
  "Community sharing",
] as const;

/* ─── Developer cards ─── */

export const DEVELOPER_CARDS = [
  {
    iconKey: "import",
    gradient: "from-sky-500 to-cyan-600",
    glowGradient: "from-sky-500/5 via-transparent to-cyan-500/5",
    title: "npm Package",
    desc: "Install via npm with full TypeScript support, React component, event system, and tree-shaking.",
    isNew: true,
    npmUrl: "https://www.npmjs.com/package/botbyte-ai",
  },
  {
    iconKey: "plug",
    gradient: "from-indigo-500 to-violet-600",
    glowGradient: "from-indigo-500/5 via-transparent to-violet-500/5",
    title: "REST API",
    desc: "Full CRUD for chatbots, conversations, messages and files. Scoped API keys with request tracking.",
  },
  {
    iconKey: "webhook",
    gradient: "from-emerald-500 to-teal-600",
    glowGradient: "from-emerald-500/5 via-transparent to-teal-500/5",
    title: "Real-Time Webhooks",
    desc: "Subscribe to events like message.created, conversation.started, chatbot.error — with HMAC-signed payloads and retry logic.",
    events: ["message.created", "conversation.started", "chatbot.error"],
  },
  {
    iconKey: "analytics",
    gradient: "from-pink-500 to-rose-600",
    glowGradient: "from-pink-500/5 via-transparent to-rose-500/5",
    title: "Analytics Engine",
    desc: "Deep insights into message volume, response times, user growth, peak hours, model distribution and satisfaction.",
    metrics: [
      { label: "Messages", val: "12.4k" },
      { label: "Avg Response", val: "1.2s" },
      { label: "Satisfaction", val: "94%" },
      { label: "Active Users", val: "847" },
    ],
  },
] as const;

/* ─── How It Works steps ─── */

export const STEPS: StepItem[] = [
  {
    step: "01",
    title: "Build with AI",
    desc: "Create chatbots from files & URLs, generate websites from prompts, or design workflows with drag-and-drop.",
    iconKey: "sparkles",
    gradient: "from-indigo-500 to-violet-600",
  },
  {
    step: "02",
    title: "Connect & deploy",
    desc: "Deploy to your website, Slack, Discord, WhatsApp or Telegram. Preview websites in a live sandbox.",
    iconKey: "zap",
    gradient: "from-emerald-500 to-teal-600",
  },
  {
    step: "03",
    title: "Iterate & scale",
    desc: "Track analytics, refine with AI chat, version-control changes and automate with visual workflows.",
    iconKey: "analytics",
    gradient: "from-pink-500 to-rose-600",
  },
];

/* ─── Stats ─── */

export const STATS: StatItem[] = [
  { value: "3", numericValue: 3, label: "AI Providers", sub: "GPT · Claude · Gemini" },
  { value: "5+", numericValue: 5, suffix: "+", label: "Platforms", sub: "Website · Slack · Discord · more" },
  { value: "∞", label: "AI Web Builder", sub: "Prompt → Website" },
  { value: "12", numericValue: 12, label: "Workflow Nodes", sub: "Triggers · AI · Logic · Actions" },
];

/* ─── Features grid ─── */

const OPENAI_SVG_PATH =
  "M239.184 106.203a64.716 64.716 0 0 0-5.576-53.103C219.452 28.459 191 15.784 163.213 21.74A65.586 65.586 0 0 0 52.096 45.22a64.716 64.716 0 0 0-43.23 31.36c-14.31 24.602-11.061 55.634 8.033 76.74a64.665 64.665 0 0 0 5.525 53.102c14.174 24.65 42.644 37.324 70.446 31.36a64.72 64.72 0 0 0 48.754 21.744c28.481.025 53.714-18.361 62.414-45.481a64.767 64.767 0 0 0 43.229-31.36c14.137-24.558 10.875-55.423-8.083-76.483Z";

export const FEATURES: FeatureItem[] = [
  {
    iconKey: "webBuilder", bg: "bg-indigo-50 text-indigo-600", glow: "bg-indigo-500/5",
    title: "AI Website Builder",
    desc: "Describe a website and AI builds it instantly. Live E2B sandbox preview, VS Code-style editor, iterative chat refinement.",
    isNew: true,
  },
  {
    iconKey: "workflow", bg: "bg-emerald-50 text-emerald-600", glow: "bg-emerald-500/5",
    title: "Visual Workflows",
    desc: "Drag-and-drop no-code workflow builder with 12 node types — triggers, AI processing, logic branches and actions.",
    isNew: true,
  },
  {
    iconKey: "", bg: "bg-violet-50 text-violet-600", glow: "bg-violet-500/5",
    title: "Multi-Model AI",
    desc: "Powered by OpenAI GPT, Claude Sonnet/Opus, and Google Gemini. Switch models per chatbot — use your own keys.",
    svgPath: OPENAI_SVG_PATH,
  },
  {
    iconKey: "zap", bg: "bg-amber-50 text-amber-600", glow: "bg-amber-500/5",
    title: "Platform Integrations",
    desc: "Deploy chatbots to Slack, Discord, WhatsApp & Telegram with one click. Auto-configured webhooks.",
  },
  {
    iconKey: "plug", bg: "bg-cyan-50 text-cyan-600", glow: "bg-cyan-500/5",
    title: "REST API & Webhooks",
    desc: "Full programmatic REST API with scoped keys, real-time webhook events and JavaScript SDK.",
  },
  {
    iconKey: "analytics", bg: "bg-pink-50 text-pink-600", glow: "bg-pink-500/5",
    title: "Analytics Dashboard",
    desc: "Track messages, conversations, response times, user growth, peak hours and model usage — all in real time.",
  },
  {
    iconKey: "sparkles", bg: "bg-purple-50 text-purple-600", glow: "bg-purple-500/5",
    title: "Template Marketplace",
    desc: "Browse and import pre-built chatbot templates for support, e-commerce, SaaS and more. One-click deploy.",
    isNew: true,
  },
  {
    iconKey: "import", bg: "bg-teal-50 text-teal-600", glow: "bg-teal-500/5",
    title: "RAG Knowledge Base",
    desc: "Intelligent retrieval-augmented generation from your crawled pages and uploaded files for pinpoint answers.",
  },
  {
    iconKey: "key", bg: "bg-orange-50 text-orange-600", glow: "bg-orange-500/5",
    title: "Bring Your API Key",
    desc: "No markup on AI usage — plug in your own key from OpenAI, Anthropic or Google and pay them directly.",
  },
  {
    iconKey: "folder", bg: "bg-slate-100 text-slate-600", glow: "bg-slate-500/5",
    title: "Lead Collection & Files",
    desc: "Capture leads from chat and let users upload CSV, PDF, images & more — analyzed in real time.",
  },
  {
    iconKey: "settings", bg: "bg-rose-50 text-rose-600", glow: "bg-rose-500/5",
    title: "Full Customization",
    desc: "Brand colors, welcome messages, system prompts, response length, temperature and advanced model settings.",
  },
  {
    iconKey: "dashboard", bg: "bg-sky-50 text-sky-600", glow: "bg-sky-500/5",
    title: "npm Package",
    desc: "npm install botbyte-ai — TypeScript SDK with React component, streaming, event system and CDN global build.",
    isNew: true,
  },
];

/* ─── Integrations ─── */

export const INTEGRATIONS: IntegrationItem[] = [
  {
    name: "Slack",
    iconKey: "slack",
    gradient: "from-[#4A154B] to-[#611f69]",
    bg: "bg-[#4A154B]/10",
    desc: "Respond in channels & DMs. Events API integration with slash command support.",
  },
  {
    name: "Discord",
    iconKey: "discord",
    gradient: "from-[#5865F2] to-[#4752C4]",
    bg: "bg-[#5865F2]/10",
    desc: "Slash commands, channel messages & DMs. Automatic command registration.",
  },
  {
    name: "WhatsApp",
    iconKey: "whatsapp",
    gradient: "from-[#25D366] to-[#128C7E]",
    bg: "bg-[#25D366]/10",
    desc: "Meta Cloud API. Read receipts, typing indicators & rich text messages.",
  },
  {
    name: "Telegram",
    iconKey: "telegram",
    gradient: "from-[#0088cc] to-[#229ED9]",
    bg: "bg-[#0088cc]/10",
    desc: "Auto webhook setup. Markdown replies, /start commands & inline keyboards.",
  },
];

export const WEBSITE_PLATFORMS: WebsitePlatformItem[] = [
  { name: "WordPress", src: "/wordpress.svg" },
  { name: "Shopify", src: "/shopify.svg" },
  { name: "Squarespace", src: "/squarespace.svg" },
  { name: "Wix", src: "/wix.svg" },
];
